import ParseCSBin from '@/features/oui/legacy-csbin/ParseCSBin'
import SystemData from './SystemData'
import { hydrateIntoInstance } from './jsonHydrator'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function normalizeLegacyOuiSystemDataJson(source: unknown): unknown {
  if (!isRecord(source)) return source

  const normalized: Record<string, unknown> = { ...source }
  const totalSetting = isRecord(source.totalsetting) ? { ...source.totalsetting } : {}

  const totalSettingFieldMap: Record<string, string> = {
    fontname: 'FontName',
    fontsize: 'FontSize',
    istitle: 'IsTitle',
    enterse: 'EnterSE',
    movese: 'MoveSE',
    cancelse: 'CancelSE',
    warningse: 'WarningSE',
    ico: 'Ico',
    authorwords: 'AuthorWords',
    authorwordtime: 'AuthorWordTime',
    auto: 'Auto',
    startmapid: 'StartMapId',
    fontcolor_msg: 'FontColor_MSG',
    fontcolor_ui: 'FontColor_UI',
    fontstyle: 'FontStyle',
    init_savedata: 'Init_savedata',
    menuindex: 'MenuIndex',
    menucodescenename: 'MenuCodeSceneName',
    openvit: 'OpenVit',
    isclosecgbgm: 'IsCloseCGBGM',
    show_ad: 'Show_ad',
    fontsettinglist: 'FontSettingList',
    testflowers: 'TestFlowers',
    isseniorstyle: 'IsSeniorStyle',
    isstroke: 'IsStroke',
    strokecolor: 'StrokeColor',
    strokewidth: 'StrokeWidth',
    isshadow: 'IsShadow',
    shadowcolor: 'ShadowColor',
    shadowwidth: 'ShadowWidth',
  }

  for (const [sourceKey, targetKey] of Object.entries(totalSettingFieldMap)) {
    if (source[sourceKey] === undefined) continue
    totalSetting[targetKey] = sourceKey === 'show_ad' && typeof source[sourceKey] === 'boolean'
      ? Number(source[sourceKey])
      : source[sourceKey]
  }
  normalized.totalSetting = totalSetting

  const btnList = isRecord(source.btnlist) ? { ...source.btnlist } : {}
  if (source.buttons !== undefined) {
    btnList.buttons = source.buttons
  }
  normalized.btnList = btnList

  const customUI = isRecord(source.customui) ? { ...source.customui } : {}
  if (source.cuilist !== undefined) {
    customUI.CUIList = source.cuilist
  }
  normalized.customUI = customUI

  const floatButton = isRecord(source.floatbutton) ? { ...source.floatbutton } : {}
  if (source.fbtnlist !== undefined) {
    floatButton.FBtnList = source.fbtnlist
  }
  normalized.floatButton = floatButton

  const bubblePersion = isRecord(source.bubblepersion) ? { ...source.bubblepersion } : {}
  if (source.personlist !== undefined) {
    bubblePersion.PersonList = source.personlist
  }
  normalized.bubblePersion = bubblePersion

  return normalized
}

function readRecord(source: Record<string, unknown>, key: string): unknown {
  return source[key] ?? source[key.toLowerCase()] ?? source[key.toUpperCase()]
}

function hydrateBucket<T extends object>(
  target: T,
  source: unknown,
  path: string[],
): void {
  hydrateIntoInstance(target, source, path)
}

export function resetSystemData(target: SystemData): void {
  const fresh = new SystemData()
  target.CG = fresh.CG
  target.BGM = fresh.BGM
  target.Sldata = fresh.Sldata
  target.Talk = fresh.Talk
  target.Title = fresh.Title
  target.GameMenu = fresh.GameMenu
  target.btnList = fresh.btnList
  target.backPlay = fresh.backPlay
  target.IsShowSystemMenu = fresh.IsShowSystemMenu
  target.SystemMenu = fresh.SystemMenu
  target.totalSetting = fresh.totalSetting
  target.customUI = fresh.customUI
  target.floatButton = fresh.floatButton
  target.bubblePersion = fresh.bubblePersion
}

function applyLegacyTotalSetting(systemData: SystemData, source: Record<string, unknown>): void {
  hydrateBucket(systemData.totalSetting, readRecord(source, 'totalSetting'), ['totalSetting'])

  const legacyFieldMap: Record<string, keyof SystemData['totalSetting']> = {
    fontname: 'FontName',
    fontsize: 'FontSize',
    istitle: 'IsTitle',
    enterse: 'EnterSE',
    movese: 'MoveSE',
    cancelse: 'CancelSE',
    warningse: 'WarningSE',
    ico: 'Ico',
    authorwords: 'AuthorWords',
    authorwordtime: 'AuthorWordTime',
    auto: 'Auto',
    startmapid: 'StartMapId',
    fontcolor_msg: 'FontColor_MSG',
    fontcolor_ui: 'FontColor_UI',
    fontstyle: 'FontStyle',
    init_savedata: 'Init_savedata',
    menuindex: 'MenuIndex',
    menucodescenename: 'MenuCodeSceneName',
    openvit: 'OpenVit',
    isclosecgbgm: 'IsCloseCGBGM',
    show_ad: 'Show_ad',
    fontsettinglist: 'FontSettingList',
    testflowers: 'TestFlowers',
    isseniorstyle: 'IsSeniorStyle',
    isstroke: 'IsStroke',
    strokecolor: 'StrokeColor',
    strokewidth: 'StrokeWidth',
    isshadow: 'IsShadow',
    shadowcolor: 'ShadowColor',
    shadowwidth: 'ShadowWidth',
  }

  const totalSettingRecord = systemData.totalSetting as unknown as Record<string, unknown>
  for (const [sourceKey, targetKey] of Object.entries(legacyFieldMap)) {
    const value = source[sourceKey]
    if (value === undefined) continue
    const nextValue = sourceKey === 'show_ad' && typeof value === 'boolean' ? Number(value) : value
    hydrateBucket(systemData.totalSetting, { [targetKey]: nextValue }, ['totalSetting'])
  }

  if (!Number.isFinite(Number(systemData.totalSetting.TestFlowers))) {
    systemData.totalSetting.TestFlowers = 2000
  }
}

function applyLegacyRootBuckets(systemData: SystemData, source: Record<string, unknown>): void {
  hydrateBucket(systemData.btnList, readRecord(source, 'btnList'), ['btnList'])
  if (source.buttons !== undefined) {
    hydrateBucket(systemData.btnList, { buttons: source.buttons }, ['btnList'])
  }

  hydrateBucket(systemData.customUI, readRecord(source, 'customUI'), ['customUI'])
  if (source.cuilist !== undefined) {
    hydrateBucket(systemData.customUI, { CUIList: source.cuilist }, ['customUI'])
  }

  hydrateBucket(systemData.floatButton, readRecord(source, 'floatButton'), ['floatButton'])
  if (source.fbtnlist !== undefined) {
    hydrateBucket(systemData.floatButton, { FBtnList: source.fbtnlist }, ['floatButton'])
  }

  hydrateBucket(systemData.bubblePersion, readRecord(source, 'bubblePersion'), ['bubblePersion'])
  if (source.personlist !== undefined) {
    hydrateBucket(systemData.bubblePersion, { PersonList: source.personlist }, ['bubblePersion'])
  }
}

function applyLegacySystemModules(systemData: SystemData, source: Record<string, unknown>): void {
  type LegacyModuleKey = 'CG' | 'BGM' | 'Sldata' | 'Talk' | 'Title' | 'GameMenu' | 'backPlay' | 'SystemMenu'

  const directBuckets: Array<[LegacyModuleKey, string]> = [
    ['CG', 'cg'],
    ['BGM', 'bgm'],
    ['Sldata', 'sldata'],
    ['Talk', 'talk'],
    ['Title', 'title'],
    ['GameMenu', 'gamemenu'],
    ['backPlay', 'backplay'],
    ['SystemMenu', 'systemmenu'],
  ]

  for (const [targetKey, sourceKey] of directBuckets) {
    hydrateBucket(systemData[targetKey], readRecord(source, sourceKey), [String(targetKey)])
  }

  if (source.isshowsystemmenu !== undefined) {
    systemData.IsShowSystemMenu = Boolean(source.isshowsystemmenu)
  }
}

function applyLegacySystemDataJson(target: SystemData, source: Record<string, unknown>): void {
  resetSystemData(target)
  const systemData = target
  applyLegacySystemModules(systemData, source)
  applyLegacyTotalSetting(systemData, source)
  applyLegacyRootBuckets(systemData, source)
}

export function parseLegacyOuiSystemDataInto(target: SystemData, bytes: Uint8Array): void {
  const parser = new ParseCSBin(bytes)
  const rawJson = parser.toJson(ParseCSBin.OUI)
  const normalizedJson = normalizeLegacyOuiSystemDataJson(rawJson)
  if (!isRecord(normalizedJson)) {
    throw new Error('[oui] legacy Sys ParseCSBin result is not an object')
  }

  applyLegacySystemDataJson(target, normalizedJson)
}
